import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, Trash2, Mail, Phone, Plus, Save, AlertTriangle } from "lucide-react";

const TYPE_OPTIONS = [
  { v: "fournisseur", l: "Fournisseur" },
  { v: "prestataire", l: "Prestataire / Sous-traitant" },
  { v: "client", l: "Client" },
  { v: "partenaire", l: "Partenaire" },
  { v: "prospect", l: "Prospect" },
];

const PARTENAIRE_TYPES = [
  { v: "", l: "—" },
  { v: "franchiseur", l: "Franchiseur" },
  { v: "apporteur", l: "Apporteur d'affaires" },
  { v: "consultant", l: "Consultant" },
];

const CATEGORY_SUGGESTIONS = [
  "Agencement cuisine", "Agencement magasin", "Poseur mobilier", "Menuiserie bois",
  "Métallerie", "Enseigne lumineuse", "Signalétique adhésive", "Bureau de contrôle ERP",
  "Prévention sécurité", "Luminaire naturel", "Mobilier CHR", "Mobilier sur mesure",
  "Revêtements muraux", "Carrelage", "Peinture", "Plomberie", "Électricité",
  "Climatisation", "Graphisme", "Photographie", "Communication",
];

const FIELDS_LEFT = [
  { k: "nom", l: "Nom contact / société", required: true },
  { k: "societe", l: "Société (si différente)" },
  { k: "poste", l: "Poste / fonction" },
  { k: "email", l: "Email", type: "email" },
  { k: "telephone", l: "Téléphone" },
  { k: "site", l: "Site internet" },
  { k: "linkedin", l: "LinkedIn (profil/page)" },
  { k: "adresse", l: "Adresse" },
  { k: "ville", l: "Ville" },
  { k: "zone", l: "Zone géographique" },
];

const FIELDS_RIGHT = [
  { k: "categorie", l: "Catégorie principale" },
  { k: "sous_categorie", l: "Sous-catégorie métier" },
  { k: "keywords", l: "Mots-clés (recherche)" },
  { k: "conditions", l: "Conditions commerciales" },
  { k: "remises", l: "Remises / tarifs négociés" },
  { k: "identifiants_pro", l: "Accès pro / identifiants" },
  { k: "liens_utiles", l: "Liens utiles (catalogue, tarif…)" },
  { k: "dernier_contact", l: "Dernier contact", type: "date" },
  { k: "prochaine_relance", l: "Prochaine relance", type: "date" },
];

const NOTES_FIELDS = [
  { k: "notes", l: "Notes" },
  { k: "notes_internes", l: "Notes internes" },
  { k: "commentaires", l: "Commentaires" },
];

export default function ContactDetailModal({ item, onClose, onSaved, onDeleted }) {
  const [data, setData] = useState(() => normalize(item));
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deps, setDeps] = useState(null);
  const [newField, setNewField] = useState({ k: "", v: "" });

  useEffect(() => { setData(normalize(item)); setConfirmDel(false); setDeps(null); }, [item]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setCustom = (k, v) => setData((d) => ({ ...d, custom_fields: { ...(d.custom_fields || {}), [k]: v } }));
  const removeCustom = (k) => setData((d) => {
    const cf = { ...(d.custom_fields || {}) };
    delete cf[k];
    return { ...d, custom_fields: cf };
  });

  const toggleType = (t) => {
    setData((d) => {
      const types = new Set(d.types || []);
      if (types.has(t)) types.delete(t); else types.add(t);
      return { ...d, types: Array.from(types) };
    });
  };

  const save = async () => {
    if (!data.nom || !data.nom.trim()) {
      alert("Le nom est obligatoire.");
      return;
    }
    if (!data.types || data.types.length === 0) {
      alert("Sélectionnez au moins un type de fiche.");
      return;
    }
    setSaving(true);
    try {
      const { data: updated } = await api.put(`/contacts/${item.id}`, data);
      onSaved?.(updated);
      onClose();
    } catch (e) {
      alert("Erreur : " + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const askDelete = async () => {
    try {
      const { data: depResp } = await api.get(`/contacts/${item.id}/dependencies`);
      setDeps(depResp);
    } catch { setDeps({ projects: [], count: 0 }); }
    setConfirmDel(true);
  };

  const del = async (force = false) => {
    setSaving(true);
    try {
      await api.delete(`/contacts/${item.id}`, { params: force ? { force: true } : {} });
      onDeleted?.(item.id);
      onClose();
    } catch (e) {
      if (e.response?.status === 409) {
        setDeps(e.response.data?.detail?.dependencies || { count: 0, projects: [] });
        setConfirmDel(true);
      } else {
        alert("Erreur : " + (e.response?.data?.detail || e.message));
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-4xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="contact-detail-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              {(data.types || []).map((t) => (
                <span key={t} className="pill bg-stone-100 text-stone-700 capitalize" data-testid={`cd-type-pill-${t}`}>{t}</span>
              ))}
              {data.categorie && <span className="pill bg-rose-50 text-rose-800">{data.categorie}</span>}
            </div>
            <h3 className="font-display text-2xl font-bold tracking-tight">{data.nom || "(sans nom)"}</h3>
            {data.societe && data.societe !== data.nom && <div className="text-sm text-stone-500">{data.societe}</div>}
          </div>
          <div className="flex items-center gap-2">
            {data.email && (
              <a href={`mailto:${data.email}`} data-testid="cd-mailto" className="w-9 h-9 rounded-full border border-stone-200 hover:bg-stone-50 flex items-center justify-center text-stone-700"><Mail size={15}/></a>
            )}
            {data.telephone && (
              <a href={`tel:${data.telephone.replace(/\s/g, '')}`} data-testid="cd-callto" className="w-9 h-9 rounded-full border border-stone-200 hover:bg-stone-50 flex items-center justify-center text-stone-700"><Phone size={15}/></a>
            )}
            <button onClick={onClose} className="w-9 h-9 rounded-full border border-stone-200 hover:bg-stone-50 flex items-center justify-center"><X size={16}/></button>
          </div>
        </div>

        {/* Multi-type selector */}
        <div className="mb-5 p-4 border border-stone-200 rounded-xl bg-stone-50">
          <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-2">Type(s) de fiche · Multi-sélection possible</div>
          <div className="flex flex-wrap gap-2">
            {TYPE_OPTIONS.map((opt) => {
              const active = (data.types || []).includes(opt.v);
              return (
                <button
                  key={opt.v}
                  type="button"
                  data-testid={`cd-type-toggle-${opt.v}`}
                  onClick={() => toggleType(opt.v)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    active
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
                  }`}
                >
                  {opt.l}
                </button>
              );
            })}
          </div>
          {(data.types || []).includes("partenaire") && (
            <label className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Type de partenaire :</span>
              <select value={data.partenaire_type || ""} onChange={(e) => set("partenaire_type", e.target.value)} data-testid="cd-partenaire-type" className="border border-stone-200 rounded-lg px-2 py-1 text-xs">
                {PARTENAIRE_TYPES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
              <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold ml-3">Taux commission %</span>
              <input type="number" step="0.1" value={data.taux_commission || 0} onChange={(e) => set("taux_commission", parseFloat(e.target.value) || 0)} data-testid="cd-taux" className="w-20 border border-stone-200 rounded-lg px-2 py-1 text-xs"/>
            </label>
          )}
          {(data.types || []).includes("prestataire") && (
            <label className="mt-3 flex items-center gap-2 text-xs">
              <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">Métier prestataire :</span>
              <input value={data.metier || ""} onChange={(e) => set("metier", e.target.value)} placeholder="ex: bureau_controle, graphiste…" data-testid="cd-metier" className="flex-1 border border-stone-200 rounded-lg px-2 py-1 text-xs"/>
            </label>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Identité & contact</div>
            {FIELDS_LEFT.map((f) => (
              <Field key={f.k} f={f} value={data[f.k] || ""} onChange={(v) => set(f.k, v)}/>
            ))}
          </div>
          <div className="space-y-3">
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Catégorisation & qualification</div>
            {FIELDS_RIGHT.map((f) => (
              <Field key={f.k} f={f} value={data[f.k] || ""} onChange={(v) => set(f.k, v)} suggestions={f.k === "categorie" ? CATEGORY_SUGGESTIONS : undefined}/>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="mt-5 space-y-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Notes</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {NOTES_FIELDS.map((f) => (
              <label key={f.k} className="flex flex-col">
                <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1">{f.l}</span>
                <textarea value={data[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} rows={3} data-testid={`cd-${f.k}`} className="border border-stone-200 rounded-lg px-3 py-2 text-sm resize-none"/>
              </label>
            ))}
          </div>
        </div>

        {/* Custom fields */}
        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-2">Champs personnalisés</div>
          <div className="space-y-2">
            {Object.entries(data.custom_fields || {}).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2" data-testid={`cd-custom-${k}`}>
                <span className="text-xs font-semibold text-stone-600 min-w-[160px]">{k}</span>
                <input value={v} onChange={(e) => setCustom(k, e.target.value)} className="flex-1 border border-stone-200 rounded-lg px-3 py-1.5 text-sm"/>
                <button onClick={() => removeCustom(k)} className="text-stone-400 hover:text-red-600"><Trash2 size={14}/></button>
              </div>
            ))}
            <div className="flex items-center gap-2 pt-2 border-t border-stone-100">
              <input placeholder="Nom du champ" value={newField.k} onChange={(e) => setNewField({ ...newField, k: e.target.value })} data-testid="cd-custom-key" className="border border-stone-200 rounded-lg px-3 py-1.5 text-sm w-44"/>
              <input placeholder="Valeur" value={newField.v} onChange={(e) => setNewField({ ...newField, v: e.target.value })} data-testid="cd-custom-value" className="flex-1 border border-stone-200 rounded-lg px-3 py-1.5 text-sm"/>
              <button
                data-testid="cd-custom-add"
                onClick={() => { if (newField.k) { setCustom(newField.k, newField.v); setNewField({ k: "", v: "" }); } }}
                className="text-xs px-3 py-1.5 bg-stone-900 text-white rounded-lg font-semibold flex items-center gap-1"
              ><Plus size={12}/> Ajouter</button>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 pt-4 border-t border-stone-100 flex items-center justify-between flex-wrap gap-2">
          {!confirmDel ? (
            <button data-testid="cd-delete" onClick={askDelete} className="text-stone-500 hover:text-red-700 text-sm font-semibold flex items-center gap-1.5">
              <Trash2 size={14}/> Supprimer cette fiche
            </button>
          ) : (
            <div className="flex flex-col gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm w-full md:max-w-2xl">
              <div className="flex items-start gap-2 text-amber-900">
                <AlertTriangle size={16} className="mt-0.5 shrink-0"/>
                <div className="space-y-1">
                  {deps?.count > 0 ? (
                    <>
                      <div className="font-semibold">Ce contact est lié à {deps.count} projet{deps.count > 1 ? "s" : ""} :</div>
                      <ul className="list-disc ml-5 text-xs">
                        {deps.projects.slice(0, 6).map((p) => <li key={p.id}>{p.nom}</li>)}
                      </ul>
                      <div className="text-xs">Le supprimer retirera automatiquement ces rattachements.</div>
                    </>
                  ) : (
                    <div>Confirmer la suppression de cette fiche ?</div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 justify-end">
                <button onClick={() => setConfirmDel(false)} className="text-stone-700 px-3 py-1 text-xs rounded border border-stone-300">Annuler</button>
                <button data-testid="cd-delete-confirm" onClick={() => del(deps?.count > 0)} disabled={saving} className="bg-stone-900 text-white px-3 py-1 text-xs rounded font-semibold">
                  {deps?.count > 0 ? "Supprimer quand même" : "Confirmer la suppression"}
                </button>
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm border border-stone-200 rounded-lg">Fermer</button>
            <button data-testid="cd-save" onClick={save} disabled={saving} className="px-4 py-2 text-sm bg-stone-900 hover:bg-black text-white rounded-lg font-semibold flex items-center gap-1.5">
              <Save size={14}/> Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function normalize(item) {
  const x = { ...item };
  // Backward-compatibility: derive types from legacy single 'type' field if missing
  if (!x.types || x.types.length === 0) {
    if (x.type) x.types = [x.type];
    else x.types = [];
  }
  return x;
}

function Field({ f, value, onChange, suggestions }) {
  const listId = suggestions ? `dl-${f.k}` : undefined;
  return (
    <label className="flex flex-col">
      <span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1">{f.l}{f.required && " *"}</span>
      <input
        type={f.type || "text"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={f.required}
        list={listId}
        data-testid={`cd-${f.k}`}
        className="border border-stone-200 rounded-lg px-3 py-2 text-sm"
      />
      {suggestions && (
        <datalist id={listId}>
          {suggestions.map((s) => <option key={s} value={s}/>)}
        </datalist>
      )}
    </label>
  );
}
